import { Table, Button, Row, Col, Form } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaDownload} from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import Paginate from '../../components/Paginate';
import { 
  useGetNotesQuery, 
  useDeleteNoteMutation, 
  useCreateNoteMutation 
} from '../../slices/notesApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';


const NoteListScreen = () => {
  const { pageNumber } = useParams();
  const { data, isLoading, error, refetch } = useGetNotesQuery({ pageNumber });
  const [deleteNote, { isLoading: loadingDelete }] = useDeleteNoteMutation();
  const [createNote, { isLoading: loadingCreate }] = useCreateNoteMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  

  // NEW: Date range state for filtering total notes
  // Default start date is set to a fixed value (adjust as needed),
  // and default end date is today.
  const [date1, setDate1] = useState('2023-01-01');
  const [date2, setDate2] = useState(new Date().toISOString().split('T')[0]);

  // Delete note handler
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await deleteNote(id);
        toast.success('Note deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Create note handler
  const createNoteHandler = async () => {
    if (window.confirm('Are you sure you want to create a new note?')) {
      try {
        const newNote = {
          date: new Date().toISOString().split('T')[0], // Default to today
          status: 'Pending',
          accountName: 'Default Account',
          details: 'New Note',
          number: 0,
        };
        await createNote(newNote).unwrap();
        toast.success('New note created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Sorting handler
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  const filterAndSortNotes = () => {
    let filteredNotes = data.notes;

    // Filter by keyword
    if (filter) {
      filteredNotes = filteredNotes.filter((note) =>
        note.status.toLowerCase().includes(filter.toLowerCase()) ||
        note.username.toLowerCase().includes(filter.toLowerCase()) ||
        note.accountName.toLowerCase().includes(filter.toLowerCase()) ||
        note.details.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Sorting based on sortField
    if (sortField) {
      filteredNotes.sort((a, b) => {
        if (sortField === 'number') {
          return sortOrder === 'asc'
            ? a.number - b.number
            : b.number - a.number;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          return sortOrder === 'asc'
            ? a[sortField].localeCompare(b[sortField])
            : b[sortField].localeCompare(a[sortField]);
        }
      });
    }

    return filteredNotes;
  };

  // NEW: Calculate total notes (sum of number field) for notes within date range.
  // Only include notes if note.date >= date1 AND note.date < date2.
  const getTotalFilteredNotes = () => {
    if (!data || !data.notes) return 0;
    const filteredNotes = data.notes.filter((note) => {
      if (!note.date) return false;
      const noteDate = new Date(note.date).toISOString().split('T')[0];
      return noteDate >= date1 && noteDate < date2;
    });
    return filteredNotes.reduce((acc, note) => acc + Number(note.number || 0), 0);
  };

  // Grand total of all notes (ignoring date filter)
  const totalNumber = data?.notes.reduce((acc, note) => acc + Number(note.number || 0), 0) || 0;
  
  const downloadNoteImages = async (note) => {
  const zip = new JSZip();
  const folder = zip.folder(`Note_${note._id}`);

  const imagePromises = (note.images || []).map(async (imagePath) => {
    try {
      const normalized = imagePath.replace(/\\/g, '/');
      const response = await fetch(normalized);
      const blob = await response.blob();

      const filename = normalized.split('/').pop();
      folder.file(filename, blob);
    } catch (error) {
      console.error(`Failed to fetch ${imagePath}`, error);
    }
  });

  await Promise.all(imagePromises);

  zip.generateAsync({ type: 'blob' }).then((zipFile) => {
    saveAs(zipFile, `Note_${note._id}_Images.zip`);
  });
};    
        
        const downloadAllNoteImages = async () => {
  if (!date1 || !date2) {
    toast.error("Please select a start and end date first");
    return;
  }

  const start = new Date(date1);
  const end = new Date(date2);

  // Filter notes by date range
  const filteredNotes =
    data?.notes?.filter((note) => {
      const noteDate = new Date(note.date);
      return noteDate >= start && noteDate <= end;
    }) || [];

  // Count images first
  const allImages = filteredNotes.flatMap((n) => n.images || []);

  if (allImages.length === 0) {
    toast.warn("No note images found in the selected date range");
    return;
  }

  setIsDownloading(true);
  setDownloadProgress(0);

  const zip = new JSZip();
  const folder = zip.folder("All_Notes_Images");

  let processed = 0;

  const imagePromises = filteredNotes.flatMap((note) =>
    (note.images || []).map(async (imagePath) => {
      try {
        const normalized = imagePath.replace(/\\/g, "/");
        const response = await fetch(normalized);
        const blob = await response.blob();

        const filename = normalized.split("/").pop();
        folder.file(filename, blob);
      } catch (error) {
        console.error(`Failed to fetch ${imagePath}`, error);
      }

      processed += 1;
      setDownloadProgress(Math.round((processed / allImages.length) * 100));
    })
  );

  await Promise.all(imagePromises);

  zip.generateAsync({ type: "blob" }).then((zipFile) => {
    saveAs(zipFile, "All_Notes_Images.zip");

    toast.success(
      `Download complete. ${allImages.length} image${allImages.length !== 1 ? "s" : ""} included.`
    );

    setIsDownloading(false);
    setDownloadProgress(0);
  });
};
        
       const exportToExcel = () => {
       if (!data || !data.notes || data.notes.length === 0) {
        toast.error("No notes available to export!");
         return;
         }

  const notesData = data.notes.map((note) => ({
    Date: new Date(note.date).toLocaleDateString(),
    Status: note.status,
    AccountName: note.accountName,
    UserName: note.username,
    Details: note.details,
    Number: note.number,
  }));

  const worksheet = XLSX.utils.json_to_sheet(notesData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Notes');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const file = new Blob([excelBuffer], { type: 'application/octet-stream' });

  saveAs(file, 'Admin_Notes.xlsx');
   };


  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Admin Notes</h1>
        </Col>
        <Col className="text-end">
        <Button
  className="my-3 mx-2"
  onClick={downloadAllNoteImages}
  disabled={isDownloading}
>
  {isDownloading ? (
    <>
      <span className="spinner-border spinner-border-sm me-2"></span>
      Zipping...
    </>
  ) : (
    <>
      <FaDownload /> Download All Note Images
    </>
  )}
</Button>

{isDownloading && (
  <div className="my-2" style={{ width: "250px" }}>
    <div className="progress" style={{ height: "8px" }}>
      <div
        className="progress-bar progress-bar-striped progress-bar-animated"
        role="progressbar"
        style={{ width: `${downloadProgress}%` }}
      ></div>
    </div>
    <small>{downloadProgress}%</small>
  </div>
)}

         <Button className="my-3 mx-2" onClick={exportToExcel}>
         <FaDownload /> Export to Excel
         </Button>
          <Button className="my-3" onClick={createNoteHandler}>
            <FaPlus /> Create Note
          </Button>
          <Link to='/create/notification' className='btn btn-light my-3'>
            Send Message
          </Link>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          <h5>Total Number (All Notes): {totalNumber}</h5>
        </Col>
        
      </Row>

      {/* NEW: Date range inputs and filtered total notes */}
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group controlId="date1">
            <Form.Label>Start Date</Form.Label>
            <Form.Control
              type="date"
              value={date1}
              onChange={(e) => setDate1(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="date2">
            <Form.Label>End Date</Form.Label>
            <Form.Control
              type="date"
              value={date2}
              onChange={(e) => setDate2(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={4} className="d-flex align-items-end">
          <h5>
            Total Notes (Filtered): {getTotalFilteredNotes()}

          </h5>
          
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by status, account name, user, or details"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error.data.message}</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort('date')}
                  style={{ cursor: 'pointer' }}
                >
                  DATE {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>STATUS</th>
                <th
                  onClick={() => handleSort('accountName')}
                  style={{ cursor: 'pointer' }}
                >
                  ACCNAME {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('username')}
                  style={{ cursor: 'pointer' }}
                >
                  USER {sortField === 'username' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('details')}
                  style={{ cursor: 'pointer' }}
                >
                  DETAILS {sortField === 'details' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th
                  onClick={() => handleSort('number')}
                  style={{ cursor: 'pointer' }}
                >
                  NUMBER {sortField === 'number' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>IMAGES</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortNotes().map((note) => (
                <tr key={note._id}>
                  <td>{new Date(note.date).toLocaleDateString()}</td>
                  <td>{note.status}</td>
                  <td>{note.accountName}</td>
                  <td>{note.username}</td>
                  <td>{note.details}</td>
                  <td>{note.number}</td>
                  <td className="thumbnails">
                    {note.images?.length > 0 && (
                      <img
                        src={note.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>
                  <td>
                    <Link
                      className="btn btn-primary btn-sm m-2"
                      to={`/note/${note._id}`}
                      role="button"
                    >
                    Details
                    </Link>
                    <Button
                      as={Link}
                      to={`/admin/note/${note._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button variant="info" className="btn-sm" onClick={() => downloadNoteImages(note)}>
                      <FaDownload /> Download Images
                    </Button>
                    <Button
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(note._id)}
                    >
                      <FaTrash style={{ color: 'white' }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          <Paginate pages={data.pages} page={data.page} isAdmin={true} type="notelist" />
        </>
      )}
    </>
  );
};

export default NoteListScreen;